import { useState, useEffect } from 'react'
import { getDashboardOverview, getSourcePages, DashboardFilters } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import { ExportButton } from '../components/ExportButton'

interface FlowPerformanceData {
  summary: {
    totalLeads: number
    totalSpend: number
    totalRevenue: number
    profit: number
  }
  kpis: {
    roi: number
    cpl: number
    rpl: number
    profitMargin: number
    validationRate: number
  }
  channels: {
    google: { spend: number; percentage: number }
    microsoft: { spend: number; percentage: number }
    facebook: { spend: number; percentage: number }
    tiktok: { spend: number; percentage: number }
  }
  sourcePages?: Array<{
    url: string
    totalLeads: number
    totalRevenue: number
    validationRate: number
  }>
  trafficSources?: Array<{
    source: string
    leads: number
    revenue: number
    cost: number
    roi: number
  }>
}

// Solo el spend viene ya desglosado por canal en getDashboardOverview
// (data.channels.google.spend, etc.) — leads/revenue/roi son del período
// COMPLETO, no de un canal. Antes la tabla "Traffic Source Performance"
// reusaba esos totales globales para las 4 filas (Google/Facebook/
// Microsoft/TikTok mostraban EXACTAMENTE el mismo "Unique Leads" y
// "Revenue"). Se piden los 4 overviews con su propio filtro "source"
// (mismo patrón que ya usan Facebook.tsx/Google.tsx/etc.) SOLO cuando no
// hay ya un filtro de fuente aplicado — si el usuario ya filtró a un
// canal concreto, `data` ya viene correctamente scoped a ese canal.
type ChannelKey = 'google' | 'facebook' | 'microsoft' | 'tiktok'
type ChannelDataMap = Partial<Record<ChannelKey, FlowPerformanceData>>

const FlowPerformance = () => {
  const [data, setData] = useState<FlowPerformanceData | null>(null)
  const [sourcePagesData, setSourcePagesData] = useState<any[]>([])
  const [channelData, setChannelData] = useState<ChannelDataMap>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState({
    startDate: '2025-09-01',
    endDate: '2025-11-30',
    source: 'all',
    type: 'all',
    flow: 'all'
  })

  const fetchData = async () => {
    try {
      setLoading(true)

      const apiFilters: DashboardFilters = {
        startDate: filters.startDate,
        endDate: filters.endDate,
        source: filters.source,
        type: filters.type,
        flow: filters.flow
      }

      console.log('🌊 Fetching Flow Performance data with filters:', apiFilters)

      const needsPerChannelData = filters.source === 'all'
      const channelKeys: ChannelKey[] = ['google', 'facebook', 'microsoft', 'tiktok']

      const [overviewResponse, sourcePagesResponse, ...channelResponses] = await Promise.all([
        getDashboardOverview(apiFilters),
        getSourcePages(apiFilters),
        ...(needsPerChannelData
          ? channelKeys.map((key) => getDashboardOverview({ ...apiFilters, source: key }))
          : [])
      ])

      setData(overviewResponse.data)
      setSourcePagesData(sourcePagesResponse.data?.sourcePages || [])
      setChannelData(
        needsPerChannelData
          ? Object.fromEntries(channelKeys.map((key, i) => [key, channelResponses[i].data]))
          : {}
      )
      setError(null)

      console.log('📊 Flow Performance data loaded:', overviewResponse.data)
      console.log('📄 Source Pages data loaded:', sourcePagesResponse.data)
    } catch (err) {
      setError('Error al cargar los datos del flow performance')
      console.error('Error fetching flow performance data:', err)
    } finally {
      setLoading(false)
    }
  }

  const resetAllFilters = () => {
    console.log('Reseteando todos los filtros')
    const defaultFilters = {
      startDate: '2025-09-01',
      endDate: '2025-11-30',
      source: 'all',
      type: 'all',
      flow: 'all'
    }
    setFilters(defaultFilters)
  }

  useEffect(() => {
    fetchData()
  }, [filters])

  if (loading) {
    return <LoadingSpinner />
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-2">⚠️</div>
          <p className="text-dashboard-muted">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary mt-4"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  if (!data) {
    return <div>No hay datos disponibles</div>
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-dashboard-text flex items-center">
            <span className="mr-3">🌊</span>
            FLOW PERFORMANCE
          </h1>
          <p className="text-dashboard-muted mt-1">
            Análisis completo del rendimiento de flujos de leads
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <ExportButton 
            type="overview"
            filters={filters}
          />
          <div className="text-right">
            <div className="text-sm text-dashboard-muted">Período</div>
            <div className="text-sm font-semibold text-dashboard-text">
              {filters.startDate} - {filters.endDate}
            </div>
          </div>
        </div>
      </div>

      {/* Filtros Básicos */}
      <div className="filter-container">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-dashboard-text">Filtros de Análisis</h3>
          <button 
            onClick={resetAllFilters}
            className="btn-secondary text-sm"
          >
            Resetear
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Inicio
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fecha Fin
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              className="filter-select w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Fuente
            </label>
            <select
              value={filters.source}
              onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}
              className="filter-select w-full"
            >
              <option value="all">Todas las fuentes</option>
              <option value="google">Google</option>
              <option value="facebook">Facebook</option>
              <option value="microsoft">Microsoft</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dashboard-text mb-2">
              Flow
            </label>
            <select
              value={filters.flow}
              onChange={(e) => setFilters(prev => ({ ...prev, flow: e.target.value }))}
              className="filter-select w-full"
            >
              <option value="all">Todos los flows</option>
              <option value="seguro-salud">Seguro Salud</option>
              <option value="creditos-personales">Créditos Personales</option>
              <option value="tarjeta-credito">Tarjeta de Crédito</option>
              <option value="seguro-decesos">Seguro Decesos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Métricas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="metric-card">
          <div className="flex items-center justify-between mb-4">
            <div className="metric-label">UNIQUE LEADS</div>
            <div className="text-2xl">👥</div>
          </div>
          <div className="metric-value">{data.summary.totalLeads.toLocaleString()}</div>
          <div className="text-sm text-dashboard-muted mt-1">Leads únicos generados</div>
        </div>

        <div className="metric-card-premium">
          <div className="flex items-center justify-between mb-4">
            <div className="metric-label-premium">TOTAL SPEND</div>
            <div className="text-2xl">💰</div>
          </div>
          <div className="metric-value-premium">€{data.summary.totalSpend.toLocaleString()}</div>
          <div className="text-sm text-globo-100 mt-1">Inversión total</div>
        </div>

        <div className="metric-card">
          <div className="flex items-center justify-between mb-4">
            <div className="metric-label">REVENUE</div>
            <div className="text-2xl">💎</div>
          </div>
          <div className="metric-value">€{data.summary.totalRevenue.toLocaleString()}</div>
          <div className="text-sm text-dashboard-muted mt-1">Ingresos generados</div>
        </div>

        <div className="metric-card">
          <div className="flex items-center justify-between mb-4">
            <div className="metric-label">PROFIT/LOSS</div>
            <div className="text-2xl">{data.summary.profit >= 0 ? '📈' : '📉'}</div>
          </div>
          <div className={`metric-value ${data.summary.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            €{data.summary.profit.toLocaleString()}
          </div>
          <div className="text-sm text-dashboard-muted mt-1">Beneficio neto</div>
        </div>
      </div>

      {/* SOURCE PAGES IN THE FLOW */}
      <div className="card">
        <div className="p-6 border-b border-dashboard-border">
          <h2 className="text-xl font-bold text-dashboard-text flex items-center">
            <span className="mr-2">🔗</span>
            SOURCE PAGES IN THE FLOW
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Source Page</th>
                <th>Unique Leads</th>
                <th>Revenue</th>
                <th>Validation Rate</th>
              </tr>
            </thead>
            <tbody>
              {sourcePagesData.length > 0 ? (
                sourcePagesData.map((page, index) => (
                  <tr key={index}>
                    <td className="font-mono text-sm">
                      {page.url || page.source_page || 'N/A'}
                    </td>
                    <td>{page.totalLeads?.toLocaleString() || page.total?.toLocaleString() || '0'}</td>
                    <td>€{page.totalRevenue?.toFixed(2) || page.revenue?.toFixed(2) || '0.00'}</td>
                    <td>{page.validationRate != null ? (page.validationRate * 100).toFixed(1) : '0.0'}%</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center text-dashboard-muted py-8">
                    {filters.source !== 'all' || filters.flow !== 'all' 
                      ? `No hay source pages para los filtros aplicados (${filters.source !== 'all' ? `Fuente: ${filters.source}` : ''}${filters.flow !== 'all' ? `Flow: ${filters.flow}` : ''})`
                      : 'No hay datos de source pages disponibles'
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* TRAFFIC SOURCE PERFORMANCE */}
      <div className="card">
        <div className="p-6 border-b border-dashboard-border">
          <h2 className="text-xl font-bold text-dashboard-text flex items-center">
            <span className="mr-2">📊</span>
            TRAFFIC SOURCE PERFORMANCE
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Source</th>
                <th>Unique Leads</th>
                <th>Revenue</th>
                <th>Cost</th>
                <th>ROI</th>
              </tr>
            </thead>
            <tbody>
              {filters.source === 'all' ? (
                // Mostrar todas las fuentes cuando no hay filtro — cada
                // una con SUS PROPIOS leads/revenue/roi (channelData),
                // no los totales globales de "data".
                <>
                  {([
                    { key: 'google' as const, label: 'Google', className: 'text-blue-600' },
                    { key: 'facebook' as const, label: 'Facebook', className: 'text-blue-800' },
                    { key: 'microsoft' as const, label: 'Microsoft', className: 'text-orange-600' },
                    { key: 'tiktok' as const, label: 'TikTok', className: 'text-black' },
                  ]).map(({ key, label, className }) => {
                    const spend = data.channels[key].spend
                    const channel = channelData[key]
                    if (spend <= 0 || !channel) return null
                    return (
                      <tr key={key}>
                        <td className={`font-semibold ${className}`}>{label}</td>
                        <td>{channel.summary.totalLeads.toLocaleString()}</td>
                        <td>€{channel.summary.totalRevenue.toLocaleString()}</td>
                        <td>€{spend.toLocaleString()}</td>
                        <td className={channel.kpis.roi >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {channel.kpis.roi.toFixed(1)}%
                        </td>
                      </tr>
                    )
                  })}
                </>
              ) : (
                // Mostrar solo la fuente filtrada
                <tr>
                  <td className={`font-semibold ${
                    filters.source === 'google' ? 'text-blue-600' :
                    filters.source === 'facebook' ? 'text-blue-800' :
                    filters.source === 'microsoft' ? 'text-orange-600' :
                    'text-black'
                  }`}>
                    {filters.source === 'google' ? 'Google' :
                     filters.source === 'facebook' ? 'Facebook' :
                     filters.source === 'microsoft' ? 'Microsoft' :
                     filters.source === 'tiktok' ? 'TikTok' : filters.source}
                  </td>
                  <td>{data.summary.totalLeads.toLocaleString()}</td>
                  <td>€{data.summary.totalRevenue.toLocaleString()}</td>
                  <td>€{data.channels[filters.source as keyof typeof data.channels]?.spend.toLocaleString() || '0'}</td>
                  <td className={data.kpis.roi >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {data.kpis.roi.toFixed(1)}%
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Costos por Canal */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center p-6">
          <div className="text-blue-600 text-2xl mb-2">🔵</div>
          <div className="text-sm text-dashboard-muted mb-1">Google Cost</div>
          <div className="text-2xl font-bold text-dashboard-text">
            €{data.channels.google.spend.toLocaleString()}
          </div>
        </div>
        <div className="card text-center p-6">
          <div className="text-blue-800 text-2xl mb-2">📘</div>
          <div className="text-sm text-dashboard-muted mb-1">Facebook Cost</div>
          <div className="text-2xl font-bold text-dashboard-text">
            €{data.channels.facebook.spend.toLocaleString()}
          </div>
        </div>
        <div className="card text-center p-6">
          <div className="text-orange-600 text-2xl mb-2">🟠</div>
          <div className="text-sm text-dashboard-muted mb-1">Microsoft Cost</div>
          <div className="text-2xl font-bold text-dashboard-text">
            €{data.channels.microsoft.spend.toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  )
}

export default FlowPerformance
